/* oxlint-disable react-doctor/rendering-hydration-mismatch-time */
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    FileDownloadOutlined as FileDownloadOutlinedIcon,
    ZoomInRounded as ZoomInRoundedIcon,
} from 'components/icons';
import SmallPdfPicture from 'assets/images/SmallPdf.png';
import { buttonStyle, GLYPH_OWN_SIZE, ICON_SLOT } from 'components/buttons/buttonStyle';
import MenuButton from 'components/buttons/MenuButton';
import CircularLoader from 'components/CircularLoader';
import { FormattedDate } from 'components/misc/FormattedDate';
import SendInvoicesButton from 'components/modals/invoices/SendInvoices';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import { TableBody, TableCell, TableRow } from 'components/ui/table';
import { Tooltip } from 'components/ui/tooltip';
import Unmasked from 'components/Unmasked';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'store';
import { useGetInvoiceTotalsQuery } from 'store/api/invoices';
import { InvoiceData, InvoiceEmailTemplateType, InvoiceForm } from 'types/invoices';
import { cn } from 'utils/cn';
import { downloadFile } from 'utils/downloadFile';
import { t } from 'utils/getTranslations';
import { useInvoice } from '../hooks/useInvoice';
import { MainPayerVatNote } from '../payers/MainPayerVatNote';
import { getInvoiceTotalsAmounts } from '../util/invoiceUtil';
import { ReasonTooltip } from './components/ReasonTooltip';
import { INVOICE_LEARNING_KEYS } from '../invoiceLearningHints';
import RecalculatingOverlay from './components/RecalculatingOverlay';
import { useInvoiceDocuments } from './useInvoiceDocuments';

interface Props {
    invoice: InvoiceData;
    isLoading: boolean;
    templateValidation: () => Promise<boolean>;
}

// 8px cells, the label column greyed; the last row draws no rule.
const CELL_CLASS = cn(SIMPLE_TABLE_CELL_CLASS, 'p-2 first:pl-2 last:pr-2');
const LABEL_CELL_CLASS = cn(CELL_CLASS, 'text-fg-secondary');
const VALUE_CELL_CLASS = cn(CELL_CLASS, 'text-right');

const DOWNLOAD_BUTTON = buttonStyle({ variant: 'outlined', size: 'small' }, 'w-full bg-paper!');
const downloadIcon = (
    <span className={ICON_SLOT.small}>
        <FileDownloadOutlinedIcon />
    </span>
);

/** The span carries the tooltip, as a disabled button fires no pointer events; it drops out of layout while enabled. */
const DisabledTooltipWrapper: React.FC<{ disabled: boolean; children: React.ReactElement }> = ({
    disabled,
    children,
}) => (
    <ReasonTooltip reason={disabled ? t('invoices.summary-sidebar.disabled-by-financial-team') : undefined}>
        <span className={disabled ? undefined : 'contents'}>{children}</span>
    </ReasonTooltip>
);

const SummarySidebar = ({ invoice, isLoading: isLoadingOther, templateValidation }: Props) => {
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const { isPrepayment } = useInvoice(invoice);
    const { data: invoiceTotals } = useGetInvoiceTotalsQuery(invoice.id);
    const {
        sentInvoicesList,
        isLoadingPreview,
        isDeleting,
        downloadingSentInvoiceIds,
        handleDownloadSentInvoice,
        openAddSentInvoiceModal,
        isEInvoiceDownloadEnabled,
        isLedesDownloadEnabled,
        isDocxDownloadEnabled,
        hasPermissionToManageSentInvoiceHistory,
        getSentInvoiceStatus,
        beforeDownload,
        handleInvoicePreviewClick,
        openDownloadEInvoiceModal,
        openDownloadLedesModal,
        formatMenuItems,
        handleDelete,
        isFinalised,
        isSentOrPaid,
        isCredited,
        isFinalisedOrSentOrPaidOrCredited,
        shouldDisableButtons,
        handleToggleDownloadButtons,
    } = useInvoiceDocuments(invoice, templateValidation);
    const isLoading = isLoadingOther || buttonsDisabled;
    const paymentCurrency = invoiceTotals?.paymentCurrency.currencyTypeCode;

    const { control } = useFormContext<InvoiceForm>();
    const [formInvoiceDate, formDueDate] = useWatch({
        control,
        name: ['invoiceDate', 'dueDate'],
    });

    const effectiveFormat = useEffectiveDateFormat();

    const { totalWithVatPaymentCurrency, totalWithoutVatPaymentCurrency } = getInvoiceTotalsAmounts(invoiceTotals);
    const vatRateAmountsPaymentCurrency = invoiceTotals?.paymentCurrency.vatRateAmounts ?? new Map();
    const invoicePrepayment = invoiceTotals?.paymentCurrency.invoice.prepaymentAmount ?? 0;

    const dataBlock: JSX.Element = (
        <>
            <table className="w-full">
                <TableBody>
                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                        <TableCell className={LABEL_CELL_CLASS}>{t('invoices.summary-sidebar.created')}</TableCell>
                        <TableCell className={VALUE_CELL_CLASS}>
                            <FormattedDate value={invoice?.createdAt} />
                        </TableCell>
                    </TableRow>
                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                        <TableCell className={LABEL_CELL_CLASS}>{t('invoices.summary-sidebar.invoice-date')}</TableCell>
                        <TableCell className={VALUE_CELL_CLASS}>
                            <FormattedDate value={formInvoiceDate || invoice?.invoiceDate} />
                        </TableCell>
                    </TableRow>
                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                        <TableCell className={LABEL_CELL_CLASS}>{t('invoices.summary-sidebar.due-date')}</TableCell>
                        <TableCell className={VALUE_CELL_CLASS}>
                            <FormattedDate value={formDueDate || invoice?.dueDate} />
                        </TableCell>
                    </TableRow>
                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                        <TableCell className={LABEL_CELL_CLASS}>{t('invoices.summary-sidebar.total-wo-vat')}</TableCell>
                        <TableCell className={VALUE_CELL_CLASS}>
                            <MoneyDisplay amount={totalWithoutVatPaymentCurrency} currency={paymentCurrency} />
                        </TableCell>
                    </TableRow>
                    {invoiceTotals ? (
                        Object.entries(vatRateAmountsPaymentCurrency).map(([vatRate, amount]) => (
                            <TableRow key={`vat-${vatRate}`} className={SIMPLE_TABLE_ROW_CLASS}>
                                <TableCell className={LABEL_CELL_CLASS}>
                                    <Unmasked>
                                        <FormattedMessage
                                            id="invoices.totals-tab.vat-rate"
                                            values={{ rate: vatRate }}
                                        />
                                    </Unmasked>
                                </TableCell>
                                <TableCell className={VALUE_CELL_CLASS}>
                                    <MoneyDisplay amount={amount} currency={paymentCurrency} />
                                </TableCell>
                            </TableRow>
                        ))
                    ) : (
                        // Holds one VAT row's height until the totals land, so the rows below do not drop
                        // when the usual single rate arrives; an invoice with none or several still moves by the difference.
                        <TableRow aria-hidden className={SIMPLE_TABLE_ROW_CLASS}>
                            <TableCell className={LABEL_CELL_CLASS}>&nbsp;</TableCell>
                            <TableCell className={VALUE_CELL_CLASS} />
                        </TableRow>
                    )}
                    {!isPrepayment && (
                        <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                            <TableCell className={LABEL_CELL_CLASS}>
                                {t('invoices.summary-sidebar.prepayment')}
                            </TableCell>
                            <TableCell className={VALUE_CELL_CLASS}>
                                <MoneyDisplay amount={invoicePrepayment * -1} currency={paymentCurrency} />
                            </TableCell>
                        </TableRow>
                    )}
                    <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                        <TableCell className={LABEL_CELL_CLASS}>{t('invoices.summary-sidebar.total-w-vat')}</TableCell>
                        <TableCell className={VALUE_CELL_CLASS}>
                            <MoneyDisplay
                                amount={totalWithVatPaymentCurrency - invoicePrepayment}
                                currency={paymentCurrency}
                            />
                        </TableCell>
                    </TableRow>
                </TableBody>
            </table>
            <MainPayerVatNote invoice={invoice} className="px-4 pt-2" />
        </>
    );

    const buttonsDisabledState = shouldDisableButtons || isLoading;

    const pdfBlock: JSX.Element = (
        <div className="bg-grey-50 relative flex h-fit flex-col p-6">
            <DisabledTooltipWrapper disabled={shouldDisableButtons}>
                <button
                    type="button"
                    aria-label={
                        isFinalisedOrSentOrPaidOrCredited
                            ? t('invoices.summary-sidebar.invoice-title')
                            : t('invoices.summary-sidebar.draft-invoice-title')
                    }
                    className="focus-visible:ring-ring/50 relative mb-4 inline-flex h-[200px] cursor-pointer items-center justify-center outline-none focus-visible:ring-3 disabled:pointer-events-none disabled:cursor-default max-sm:h-[100px] max-sm:w-full [&:disabled>*:not(.CircularLoader)]:opacity-35"
                    disabled={buttonsDisabledState || isLoadingPreview}
                    onClick={() => {
                        beforeDownload(handleInvoicePreviewClick);
                    }}
                >
                    {isLoadingPreview ? <CircularLoader /> : null}
                    <img src={SmallPdfPicture} alt="" className="block w-full object-cover px-[22px]" />
                    <span className="fill-grey-0 text-grey-900 hover:fill-grey-900 hover:text-grey-0 absolute inset-0 flex items-center justify-center">
                        <svg viewBox="0 20 420 200" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="210" cy="100" r="70" />
                            <ZoomInRoundedIcon viewBox="-23 -23 70 70" />
                        </svg>
                    </span>
                </button>
            </DisabledTooltipWrapper>
            <div className="flex flex-col gap-2">
                <DisabledTooltipWrapper disabled={shouldDisableButtons}>
                    {isDocxDownloadEnabled ? (
                        <MenuButton
                            variant="outlined"
                            className="bg-paper! w-full"
                            startIcon={<FileDownloadOutlinedIcon />}
                            size="small"
                            disabled={buttonsDisabledState}
                            menuItems={formatMenuItems(`/v1/invoices/generate/${invoice.id}`)}
                        >
                            {isFinalisedOrSentOrPaidOrCredited ? (
                                <FormattedMessage id="invoices.summary-sidebar.download-button" />
                            ) : (
                                <FormattedMessage id="invoices.summary-sidebar.download-button-draft" />
                            )}
                        </MenuButton>
                    ) : (
                        <Button
                            {...DOWNLOAD_BUTTON}
                            disabled={buttonsDisabledState}
                            onClick={() => {
                                void beforeDownload(() => downloadFile(`/v1/invoices/generate/${invoice.id}`));
                            }}
                        >
                            {downloadIcon}
                            {isFinalisedOrSentOrPaidOrCredited ? (
                                <FormattedMessage id="invoices.summary-sidebar.download-button" />
                            ) : (
                                <FormattedMessage id="invoices.summary-sidebar.download-button-draft" />
                            )}
                        </Button>
                    )}
                </DisabledTooltipWrapper>
                <DisabledTooltipWrapper disabled={shouldDisableButtons}>
                    {isDocxDownloadEnabled ? (
                        <MenuButton
                            variant="outlined"
                            className="bg-paper! w-full"
                            startIcon={<FileDownloadOutlinedIcon />}
                            size="small"
                            disabled={buttonsDisabledState}
                            menuItems={formatMenuItems(`/v1/invoices/generate-spec/${invoice.id}`)}
                        >
                            <FormattedMessage id="invoices.summary-sidebar.download-spec" />
                        </MenuButton>
                    ) : (
                        <Button
                            {...DOWNLOAD_BUTTON}
                            disabled={buttonsDisabledState}
                            onClick={() => {
                                void beforeDownload(() => downloadFile(`/v1/invoices/generate-spec/${invoice.id}`));
                            }}
                        >
                            {downloadIcon}
                            <FormattedMessage id="invoices.summary-sidebar.download-spec" />
                        </Button>
                    )}
                </DisabledTooltipWrapper>
                {isEInvoiceDownloadEnabled && isFinalisedOrSentOrPaidOrCredited && (
                    <DisabledTooltipWrapper disabled={shouldDisableButtons}>
                        <Button
                            {...DOWNLOAD_BUTTON}
                            disabled={buttonsDisabledState}
                            onClick={openDownloadEInvoiceModal}
                        >
                            {downloadIcon}
                            <FormattedMessage id="invoices.e-invoice-download.button" />
                        </Button>
                    </DisabledTooltipWrapper>
                )}
                {isLedesDownloadEnabled && isFinalisedOrSentOrPaidOrCredited && (
                    <DisabledTooltipWrapper disabled={shouldDisableButtons}>
                        <Button {...DOWNLOAD_BUTTON} disabled={buttonsDisabledState} onClick={openDownloadLedesModal}>
                            {downloadIcon}
                            <FormattedMessage id="invoices.ledes-download.button" />
                        </Button>
                    </DisabledTooltipWrapper>
                )}
                {!isFinalised && (
                    <DisabledTooltipWrapper disabled={shouldDisableButtons}>
                        <SendInvoicesButton
                            variant="outlined"
                            className="bg-paper! w-full"
                            size="small"
                            invoices={[invoice]}
                            disabled={buttonsDisabledState}
                            emailTemplateType={
                                isFinalisedOrSentOrPaidOrCredited
                                    ? InvoiceEmailTemplateType.INVOICE
                                    : InvoiceEmailTemplateType.DRAFT_INVOICE
                            }
                        >
                            {isSentOrPaid || isCredited ? (
                                <FormattedMessage id="invoices.summary-sidebar.resend-button" />
                            ) : (
                                <FormattedMessage id="invoices.summary-sidebar.send-button-draft" />
                            )}
                        </SendInvoicesButton>
                    </DisabledTooltipWrapper>
                )}
                {hasPermissionToManageSentInvoiceHistory && (isSentOrPaid || isCredited) && (
                    <Tooltip content={t('invoices.summary-sidebar.disable-downloads-tooltip')}>
                        <label
                            className={cn(
                                'text-body1 flex w-fit items-center',
                                isLoading ? 'text-fg-disabled cursor-not-allowed' : 'cursor-pointer'
                            )}
                        >
                            <Switch
                                size="sm"
                                className="data-checked:bg-warning m-2"
                                checked={invoice.downloadButtonsDisabled ?? false}
                                onCheckedChange={(checked) => {
                                    void handleToggleDownloadButtons(checked);
                                }}
                                disabled={isLoading}
                            />
                            {t('invoices.summary-sidebar.disable-downloads')}
                        </label>
                    </Tooltip>
                )}
            </div>
        </div>
    );

    const pdfHistoryBlock: JSX.Element = (
        <>
            <table className="w-full">
                <TableBody>
                    {sentInvoicesList?.map((sentInvoice) => {
                        const isDownloading = downloadingSentInvoiceIds.has(sentInvoice.id);
                        return (
                            <TableRow key={sentInvoice.id} className={SIMPLE_TABLE_ROW_CLASS}>
                                <TableCell className={LABEL_CELL_CLASS}>
                                    <div className="flex items-center gap-2">
                                        <RouterLink
                                            className={cn(
                                                'text-fg hover:text-link w-fit no-underline hover:underline',
                                                isDownloading && 'pointer-events-none opacity-60'
                                            )}
                                            to=""
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (isDownloading) return;
                                                handleDownloadSentInvoice(sentInvoice.id);
                                            }}
                                        >
                                            {format(
                                                new Date(sentInvoice?.invoiceDateTime),
                                                `${effectiveFormat}, hh:mm:ss a`
                                            )}{' '}
                                            ({getSentInvoiceStatus(sentInvoice?.invoiceStatusTypeCode)})
                                        </RouterLink>
                                        {isDownloading && (
                                            <span
                                                role="progressbar"
                                                aria-label={t('loading')}
                                                className="border-primary size-3.5 animate-spin rounded-full border-2 border-t-transparent"
                                            />
                                        )}
                                    </div>
                                </TableCell>
                                {hasPermissionToManageSentInvoiceHistory && (
                                    <TableCell className={cn(CELL_CLASS, 'w-10')}>
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            className={cn(
                                                GLYPH_OWN_SIZE,
                                                'text-error hover:bg-error-lighter hover:text-error size-[30px] rounded-sm'
                                            )}
                                            onClick={() => handleDelete(sentInvoice.id)}
                                            disabled={isDeleting || isLoading}
                                            title={t('delete')}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </Button>
                                    </TableCell>
                                )}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </table>
            {hasPermissionToManageSentInvoiceHistory && (
                <div className="mb-2 flex justify-center">
                    <Button
                        {...buttonStyle({ variant: 'outlined', size: 'small' })}
                        onClick={openAddSentInvoiceModal}
                        disabled={isLoading}
                    >
                        <span className={ICON_SLOT.small}>
                            <AddIcon />
                        </span>
                        {t('invoices.add-entry')}
                    </Button>
                </div>
            )}
        </>
    );

    return (
        <div data-learning={INVOICE_LEARNING_KEYS.summary}>
            <h3 className="text-h5 my-4">{t('invoices.summary-sidebar.title')}</h3>
            <div className="relative">
                {dataBlock}
                <RecalculatingOverlay invoiceId={invoice.id} />
            </div>
            <>
                <h3 className="text-h5 my-4">
                    {isFinalisedOrSentOrPaidOrCredited
                        ? t('invoices.summary-sidebar.invoice-title')
                        : t('invoices.summary-sidebar.draft-invoice-title')}
                </h3>
                {pdfBlock}
            </>
            <h4 className="text-subtitle1 my-4">{t('invoices.summary-sidebar.sent-invoice-history')}</h4>
            {pdfHistoryBlock}
        </div>
    );
};

export default SummarySidebar;
